import { localizePath, useLocale } from './i18n';

/**
 * Per-page metadata. React 19 hoists <title>, <meta> and <link> into <head>.
 * When the API serves the built site (SERVE_WEB=true) the same tags are also injected
 * server-side for crawlers and link previews.
 *
 * `path` is the page's address without the language prefix ("/work"); the canonical URL and the
 * English/Arabic alternates are derived from it.
 */
export function Seo({
  title,
  description,
  image,
  path,
  type = 'website',
  noindex,
  jsonLd,
}: {
  title: string;
  description?: string | null;
  image?: string | null;
  path?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
}) {
  const { locale } = useLocale();
  const origin = window.location.origin;
  const url = path ? `${origin}${localizePath(path, locale)}` : undefined;
  return (
    <>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {url && !noindex && <link rel="canonical" href={url} />}
      {path &&
        !noindex &&
        (['en', 'ar'] as const).map((l) => (
          <link key={l} rel="alternate" hrefLang={l} href={`${origin}${localizePath(path, l)}`} />
        ))}
      <meta property="og:title" content={title} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={locale === 'ar' ? 'ar_AR' : 'en_US'} />
      {description && <meta property="og:description" content={description} />}
      {image && <meta property="og:image" content={image} />}
      {url && <meta property="og:url" content={url} />}
      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\u003c') }}
        />
      )}
    </>
  );
}
